import { nothing, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../address/address-form';
import './credit-card-payment-hosted-fields';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { PaymentMethod, PaymentMethodTemplate } from '../payment-method';
import BraintreeHostedFields from './credit-card-payment-hosted-fields';
import cardLogoStyles from '../../../common/styles/card-logo-styles';
import {
  BraintreeHostedFieldsStatus,
  BusinessUnit,
  CardTypes,
  ClientTokenDetails,
  CreditCardDisplay,
  PaymentDetails,
  PaymentType,
  RemoveCreditCardRequest,
  RmsBookstoreAddress,
  VaultedCard,
} from '../../checkout/shop-checkout.interface';
import { Modal } from '../../modal/modal';
import {
  getBillingAddress,
  getBraintreeHostedFieldsStatus,
  getClientTokenResponse,
  getEditBillingAddressMode,
  getPaymentDetails,
  getRemoveCreditCardError,
  getRemoveCreditCardStatus,
  getSelectedPaymentType,
  getShippingAddress,
  isBillingAddressSameAsShippingAddress,
  isShippingAddressFromAddressbook,
  removeSavedCard,
  updateBillingAddress,
  updateBillingAddressSameAsShippingAddressFlag,
  updateBraintreeHostedFieldsStatus,
  updateDisableNextStepButton,
  updateEditBillingAddressEditMode,
  resetPaymentDetails,
  updatePaymentDetails,
} from '../../checkout/shop-checkout';
import { ActionStatus } from '../../../common/constants/action-status';
import { handleApiError } from '../../../common/utils/api-utils';
import { getVaultedCreditCardNonce } from './vaulted-card-payment-nonce';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import { isK12StorePurchaseFlow, isTunnelPurchaseFlow, wait } from '../../../common/utils/util';
import { getIntlTaxAmount, getProductsFromCart } from '../../cart/shop-cart/store/shop-cart.selectors';
import {
  getCardLogo,
  isValidBillingAddress,
  renderBillingAddressErrorMessage,
  renderCompletedAddress,
  renderCreditCardDisplay,
  renderCreditCardRemoveLink,
  renderExpiredVaultedCardInfo,
} from '../../../common/utils/payment-util';
import emit from '../../../common/events';
import { addAnalyticsCheckoutFlowMetadata, hasAtLeastOneFreeTrialProduct } from '../../../common/utils/cart-util';
import { createDataCollector, getDataCollector } from '../braintree';
import { isSegUser, selectUser } from '../../user-profile/user-profile.selectors';
import { isGuestCheckoutPage } from '../../../common/environment';
import { CREDIT_CARD_FIELDS } from '../braintree.interface';
import { waitForElementAndFocus, ariaLabelWithNewTab } from '../../../common/utils/a11y-util';

/**
 * Get client token.
 * Load new credit card hosted fields.
 * Load existing (vaulted) credit card hosted fields.
 */
@customElement(`mhecomm-credit-card-payment-method`)
export class CreditCardPaymentMethod extends connect(store)(PaymentMethod) implements PaymentMethodTemplate {
  static styles = [
    cardLogoStyles,
    css`
      @media (max-width: 340px) {
        .credit-cart-remove-link {
          margin-left: 1rem;
          margin-top: 1rem !important;
          width: 33.33% !important;
        }
      }

      @media (min-width: 340px) and (max-width: 576px) {
        .credit-cart-extra {
          order: 3;
        }

        .credit-cart-remove-link {
          order: 2;
        }
      }
    `,
  ];

  /**
   * Currency for credit card transaction.
   */
  @property()
  currency = 'USD';

  /**
   * Country code of the order. Default US.
   */
  @property()
  countryCode = 'US';

  @property()
  orderBusinessUnit: BusinessUnit = BusinessUnit.HE;

  /**
   * Braintree client token required to initialize credit card hosted fields.
   */
  @state()
  private clientToken = '';

  @state()
  private showNewCreditCardForm = false;

  @state()
  private creditCardDisplayData: CreditCardDisplay | undefined;

  @state()
  private selectedVaultedCard: VaultedCard | undefined;

  @state()
  braintreeHostedFieldsError = false;

  /**
   * Flag to indicate whether vaulted card address has been edited.
   */
  @state()
  private editVaultedBillingAddress = false;

  @state()
  private numberOfVaultedCards = 0;

  @state()
  private vaultedCreditCardNonce = '';

  private static readonly NEW_CARD_ID = 'newCard';
  private vaultEnabled = false;
  private saveInVault = false;
  private vaultedCardsList: VaultedCard[] = [];
  private threeDsEnabled = false;
  private removeSavedCardModal: Modal | undefined;
  private cardToRemove: VaultedCard | undefined;
  private deviceData;

  stateChanged(state: StoreState): void {
    this.updateIsSelected(PaymentType.BRAINTREE_CREDIT_CARD, state);

    this.editVaultedBillingAddress = getEditBillingAddressMode(state);
    if (getRemoveCreditCardStatus(state) == ActionStatus.succeeded) {
      if (this.cardToRemove) {
        if (this.selectedVaultedCard?.uniqueIdentifier === this.cardToRemove!.uniqueIdentifier) {
          this.selectedVaultedCard = undefined;
        }
        this.numberOfVaultedCards = this.vaultedCardsList.length;
        this.prepareCreditCardPaymentView();
        this.removeSavedCardModal?.close();
        this.cardToRemove = undefined;
      }
    } else if (getRemoveCreditCardStatus(state) == ActionStatus.failed) {
      this.removeSavedCardModal?.close();
      handleApiError(getRemoveCreditCardError(state));
    }
    this.billingAddress = getBillingAddress(store.getState() as StoreState) || this.billingAddressInitialState;
    if (!this.selectedVaultedCard && this.shippable) {
      this.saveInVault = true;
    } else if (
      this.selectedVaultedCard &&
      this.selectedVaultedCard.uniqueIdentifier === CreditCardPaymentMethod.NEW_CARD_ID &&
      this.shippable
    ) {
      this.saveInVault = true;
    } else if (this.selectedVaultedCard) {
      this.saveInVault = false;
    }
    if (getBraintreeHostedFieldsStatus(store.getState() as StoreState) === BraintreeHostedFieldsStatus.VALID) {
      this.braintreeHostedFieldsError = false;
    } else {
      this.braintreeHostedFieldsError = true;
    }

    if (getVaultedCreditCardNonce(state)) {
      this.vaultedCreditCardNonce = getVaultedCreditCardNonce(state);
    }
    this.isSameAsShippingAddress = isBillingAddressSameAsShippingAddress(store.getState() as StoreState);
  }

  protected async updated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(_changedProperties);
    const oldEditVaultedBillingAddress = _changedProperties.get('editVaultedBillingAddress');
    if (
      oldEditVaultedBillingAddress !== 'undefined' &&
      this.editVaultedBillingAddress &&
      !oldEditVaultedBillingAddress
    ) {
      waitForElementsToLoad(this.shadowRoot!, ['mhecomm-address-form']).then(() => {
        this.getAddressForm().setOnClickEditAddress(() => {
          store.dispatch(updateEditBillingAddressEditMode(true));
        });
      });
    }
    this.disableNextStep();
  }

  connectedCallback(): void {
    super.connectedCallback();
    const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(store.getState() as StoreState)!;
    this.showUseShippingAddressButton = isSegUser(selectUser(store.getState() as StoreState))
      ? !isShippingAddressFromAddressbook(store.getState() as StoreState)
      : true;
    this.clientToken = clientTokenResponse.token;
    this.vaultedCardsList = clientTokenResponse.vaultedCards || [];
    const paymentDetails = getPaymentDetails(store.getState() as StoreState);
    // For Shippable product by default make billing address same as shipping address
    if (this.shippable && this.showUseShippingAddressButton) {
      store.dispatch(updateBillingAddress(getShippingAddress(store.getState() as StoreState)!));
      store.dispatch(updateBillingAddressSameAsShippingAddressFlag(true));
    }
    this.isSameAsShippingAddress = isBillingAddressSameAsShippingAddress(store.getState() as StoreState);
    if (paymentDetails && paymentDetails.creditCardDisplayInfo && !paymentDetails.creditCardUniqueIdentifier) {
      const currentCard: VaultedCard = {
        cardType: paymentDetails?.creditCardDisplayInfo.cardType,
        cardLast4: paymentDetails?.creditCardDisplayInfo.cardLast4,
        bin: paymentDetails?.creditCardDisplayInfo.bin,
        uniqueIdentifier: CreditCardPaymentMethod.NEW_CARD_ID,
        billingAddress: getBillingAddress(store.getState() as StoreState)!,
        isExpired: false,
        isDefault: false,
        expiryYear: paymentDetails?.creditCardDisplayInfo.expiryYear,
        expiryMonth: paymentDetails?.creditCardDisplayInfo.expiryMonth,
        cardHolderName: paymentDetails?.creditCardDisplayInfo.cardholderName!,
      };
      this.vaultedCardsList = [currentCard, ...this.vaultedCardsList];
      this.selectedVaultedCard = currentCard;
    }
    this.numberOfVaultedCards = this.vaultedCardsList?.length;
    this.vaultEnabled = clientTokenResponse.vaultEnabled;
    this.threeDsEnabled = clientTokenResponse.threeDSecureEnabled;
    this.totalAmount = this._getTotalAmount();
    this.editVaultedBillingAddress = false;
    this.prepareCreditCardPaymentView();
    store.dispatch(updateEditBillingAddressEditMode(false));
    this.clientToken
      ? store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.VALID))
      : store.dispatch(updateBraintreeHostedFieldsStatus(BraintreeHostedFieldsStatus.INVALID));
    this.addEventListener('focus-next-step-button', this.setFocusOnEditButton.bind(this));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('focus-next-step-button', this.setFocusOnEditButton.bind(this));
  }

  setFocusOnEditButton() {
    const nextButtonId = `button#billing-edit-link`;
    waitForElementsToLoad(this.shadowRoot!, [nextButtonId]).then(() => {
      (this.shadowRoot?.querySelector(nextButtonId) as HTMLButtonElement)?.focus();
    })
  }

  /**
   * Disables the next step button if no vaulted card is selected or if the billing address is invalid.
   *
   * This is done only when the user is not trying to add a new card.
   *
   * @private
   */
  private disableNextStep(): void {
    const state = store.getState() as StoreState;
    const selectedPaymentType = getSelectedPaymentType(state);

    if (selectedPaymentType === PaymentType.BRAINTREE_CREDIT_CARD) {
      if (this.vaultedCardsList.length > 0 && !this.selectedVaultedCard && !this.showNewCreditCardForm) {
        store.dispatch(updateDisableNextStepButton(true));
        return;
      }

      const billingAddress = getBillingAddress(store.getState() as StoreState);
      if (!isValidBillingAddress(billingAddress) && !this.showNewCreditCardForm) {
        store.dispatch(updateDisableNextStepButton(true));
        return;
      }
    }

    store.dispatch(updateDisableNextStepButton(false));
  }

  _getTotalAmount() {
    let totalAmount = this.totalAmount;
    if (isTunnelPurchaseFlow() && this.threeDsEnabled) {
      totalAmount += getIntlTaxAmount(store.getState() as StoreState)!;
    }
    return Math.round(totalAmount * 100) / 100;
  }

  prepareCreditCardPaymentView() {
    this.showNewCreditCardForm = !(this.vaultEnabled && this.numberOfVaultedCards > 0);
  }

  renderComp() {
    if (!this.isSelected) {
      return nothing;
    }

    if (!this.braintreeHostedFieldsError) {
      return html` ${this.editVaultedBillingAddress
        ? html` <div>
            ${renderCreditCardDisplay(this.selectedVaultedCard!)} ${this.renderBillingAddressForm()}
            ${this.renderSaveCancelButton()}
          </div>`
        : html` <div>
            ${this.numberOfVaultedCards > 0 ? this.renderVaultedCardsForm() : nothing}
            ${this.showNewCreditCardForm ? this.renderNewCreditCardForm() : nothing}
          </div>`}`;
    } else {
      return this.renderCreditCardGenericErrorTemplate();
    }
  }

  renderNewCreditCardForm() {
    return html`
      <h3 class="fw-semibold fs-4">Add New Card</h3>
      <div class="d-flex flex-row mb-3">
        <div class="cardDisplayLogo border rounded me-2">
          <img src="${getCardLogo(CardTypes.VISA)}" class="w-75 h-75" alt="${CardTypes.VISA}" />
        </div>
        <div class="cardDisplayLogo border rounded me-2">
          <img src="${getCardLogo(CardTypes.MASTERCARD)}" class="w-75 h-75" alt="${CardTypes.MASTERCARD}" />
        </div>
        <div class="cardDisplayLogo border rounded me-2">
          <img src="${getCardLogo(CardTypes.AMEX)}" class="w-75 h-75" alt="${CardTypes.AMEX}" />
        </div>
        <div class="cardDisplayLogo border rounded me-2">
          <img src="${getCardLogo(CardTypes.DISCOVER)}" class="w-75 h-75" alt="${CardTypes.DISCOVER}" />
        </div>
      </div>
      <p><small>All fields are required unless otherwise noted.</small></p>
      <mhecomm-credit-card-payment-hosted-fields
        id="new-credit-card-hosted-fields"
        amount="${this.totalAmount}"
        clientToken="${this.clientToken}"
        ?threeDsEnabled="${this.threeDsEnabled}"
      ></mhecomm-credit-card-payment-hosted-fields>
      ${this.isHideSaveCardOption()
        ? nothing
        : html` <div class="d-inline-block mb-4 w-100 form-check}">
            <input
              class="form-check-input"
              type="checkbox"
              ?disabled="${this.isSaveCardDisabled()}"
              id="saveInVaultCheckbox"
              name="saveInVaultCheckbox"
              @change="${this.saveInVaultChangedEventHandler}"
              ?checked="${this.isSaveCardChecked()}"
            />
            <label for="saveInVaultCheckbox" class="pl-0 me-3">Save Card</label>
            <span class="d-inline-block float-sm-none">
              <mhecomm-popover
                id="payment-security"
                label="Payment Security"
                @click=${this.analyticsPaymentSecurityPopover}
                text="${this._paymentSecurityInfoText()}"
                ariaLabel="Payment Security"
                disableLinkUnderline=true
              >
              </mhecomm-popover>
            </span>
          </div>`}
      ${this.renderBillingAddressForm()}
    `;
  }

  isSaveCardDisabled() {
    const hasFreeTrial = hasAtLeastOneFreeTrialProduct(getProductsFromCart(store.getState() as StoreState));
    return this.shippable || hasFreeTrial;
  }

  isSaveCardChecked() {
    const hasFreeTrial = hasAtLeastOneFreeTrialProduct(getProductsFromCart(store.getState() as StoreState));
    return this.saveInVault || this.shippable || hasFreeTrial;
  }

  isHideSaveCardOption() {
    return isK12StorePurchaseFlow() || isGuestCheckoutPage();
  }

  _paymentSecurityInfoText() {
    if (isK12StorePurchaseFlow()) {
      return "Your payment information will be securely stored for future orders. (Please note: We don't save CVV.)";
    }

    return 'Your payment information will be securely stored only until we charge your card or at time of shipment.';
  }

  analyticsPaymentSecurityPopover() {
    emit({
      name: 'click',
      element: this,
      detail: addAnalyticsCheckoutFlowMetadata({
        eventName: 'click-payment-security',
        label: 'Payment Security',
      }),
    });
  }

  /**
   * Sort vaulted cards to show expired cards at the end.
   *
   * @param firstCard
   * @param secondCard
   * @private
   */
  private sortVaultedCards(firstCard: VaultedCard, secondCard: VaultedCard) {
    return firstCard.isExpired === secondCard.isExpired ? 0 : firstCard.isExpired ? 1 : -1;
  }

  protected saveInVaultChangedEventHandler(event: InputEvent) {
    this.saveInVault = (event.target as HTMLInputElement).checked;
  }

  renderVaultedCardsForm() {
    return html`
      ${this.vaultedCardsList
        .slice()
        .sort(this.sortVaultedCards)
        .map((vaultedCard: VaultedCard) => {
          const selected = this.isVaultedCardSelected(vaultedCard);
          return html` <div class="row px-1">
              <div class="col-10 col-sm-7 col-md-10 col-lg-5 form-check ps-0">
                <label class="form-check-label w-100" for="${vaultedCard.uniqueIdentifier}">
                  <input
                    class="form-check-input mx-2"
                    type="radio"
                    name="vaulted-card-option"
                    id="${vaultedCard.uniqueIdentifier}"
                    .checked=${selected}
                    @change=${(_e: InputEvent) => {
                      this.isSameAsShippingAddress = false;
                      this.selectedVaultedCard = vaultedCard;
                      this.showNewCreditCardForm = false;
                      const billingAddress = this.selectedVaultedCard.billingAddress as RmsBookstoreAddress;
                      if (billingAddress) {
                        store.dispatch(updateBillingAddress(billingAddress));
                      }
                    }}
                    ?disabled="${vaultedCard.isExpired}"
                    aria-label="${selected ? 'Credit card ending in ' + vaultedCard.cardLast4 + ' is selected' : ''}"
                  />
                  <span class="${vaultedCard.isExpired ? 'opacity-75' : ''}">
                    ${renderCreditCardDisplay(vaultedCard)}
                  </span>
                </label>
                <div class="ps-4 ms-2">
                  ${selected && vaultedCard.uniqueIdentifier !== CreditCardPaymentMethod.NEW_CARD_ID
                    ? this.renderVaultedCardForm()
                    : nothing}
                </div>
              </div>

              <div
                class="credit-cart-extra col-12 col-sm-5 col-md-12 col-lg-4 ms-4 ps-2 ms-sm-4 ps-sm-2 ms-lg-0 ps-lg-0 mt-3 mt-sm-0 mt-sm-3 mt-lg-0 order-sm-3 order-lg-2"
              >
                ${selected ? this.renderBillingAddress() : renderExpiredVaultedCardInfo(vaultedCard)}
              </div>

              <div class="credit-cart-remove-link col-2 col-sm-5 col-md-2 col-lg-3 order-sm-2 order-lg-3 ps-2">
                ${renderCreditCardRemoveLink(
                  (event: Event) => this.onClickRemoveCreditCardButton(event, vaultedCard),
                  this.isCurrentPayment(vaultedCard)
                )}
              </div>
            </div>
            <hr aria-hidden="true" />`;
        })}
      <div id="add-new-credit-card-link-container">
        ${!this.showNewCreditCardForm
          ? html`
            <button 
              @click="${this.onClickNewCreditCardButtonHandler}" 
              id="use-another-card" 
              class="btn btn-link p-0 new-credit-card-link">
                <span aria-hidden="true">+</span> Use another card
            </button>`
          : nothing}
      </div>
    `;
  }

  renderCreditCardGenericErrorTemplate() {
    return html`
      <div>
        <h6 class="fw-semibold fs-6 mt-2">Additional Help:</h6>
        <p class="pb-3">
          Please try the following options:<br />
          1. Wait for a couple minutes and
          <a href="javascript:window.location.href=window.location.href">refresh</a> again.<br />
          2. Use another payment method.<br />
          3. Or reach out to our <a href="https://mh.my.site.com/CSOM/s/" target="_blank" aria-label="${ariaLabelWithNewTab('customer support team')}">customer support team</a>.
        </p>
      </div>
    `;
  }

  isVaultedCardSelected(vaultedCard) {
    return this.selectedVaultedCard?.uniqueIdentifier === vaultedCard.uniqueIdentifier;
  }

  renderBillingAddress() {
    if (this.selectedVaultedCard?.billingAddress && !this.editVaultedBillingAddress) {
      const billingAddress =
        getBillingAddress(store.getState() as StoreState) || this.selectedVaultedCard?.billingAddress;
      return html`<div class="d-flex flex-row align-items-center">
        <h3 id="billing-address-header" class="fs-6 m-0 pe-4 fw-bolder">Billing Address</h3>
        ${this.selectedVaultedCard?.billingAddress
            ? this.renderEditBillingAddressButton(billingAddress)
            : nothing}</div>
        <br />
        ${isValidBillingAddress(billingAddress)
          ? renderCompletedAddress(billingAddress)
          : renderBillingAddressErrorMessage()}`;
    } else {
      return nothing;
    }
  }

  removeCreditCardModalBody(vaultedCard: VaultedCard) {
    return html`
      ${renderCreditCardDisplay(vaultedCard)}
      <p>
        If you do not want this payment method to be displayed in your list of payment options, click “Confirm remove”.
        (Disabling this payment method will not cancel any of your open orders that use this method.)
      </p>
    `;
  }

  isCurrentPayment(vaultedCard: VaultedCard) {
    return vaultedCard.uniqueIdentifier === CreditCardPaymentMethod.NEW_CARD_ID;
  }

  onClickRemoveCreditCardButton(e: Event, vaultedCard: VaultedCard) {
    e.preventDefault();
    this.analyticsRemoveVaultedCard();
    this.removeSavedCardModal = new Modal({
      header: 'Remove Payment Method',
      body: { template: this.removeCreditCardModalBody(vaultedCard) },
      primaryButton: { label: 'Confirm Delete' },
      secondaryButton: { label: 'Cancel' },
      reversedFooter: true,
      modalContext: {
        componentRef: 'remove-vaulted-card',
        componentRefData: addAnalyticsCheckoutFlowMetadata({}),
      },
    });
    this.removeSavedCardModal.addEventListener('mhecomm-modal-confirmed', async () => {
      this.cardToRemove = vaultedCard;
      this.vaultedCardsList = this.vaultedCardsList.filter((vaultedCard) => {
        return vaultedCard.uniqueIdentifier != this.cardToRemove!.uniqueIdentifier;
      });
      const removeCreditCardRequest: RemoveCreditCardRequest = {
        creditCardUniqueId: vaultedCard.uniqueIdentifier,
        currencyCode: this.currency,
        countryCode: this.countryCode,
        businessUnit: this.orderBusinessUnit,
        updatedVaultedCardsList: this.vaultedCardsList,
      };
      await store.dispatch(removeSavedCard(removeCreditCardRequest));
      this.analyticsConfirmDeleteVaultedCard();
    });
    this.removeSavedCardModal.open();
  }

  analyticsRemoveVaultedCard() {
    emit({
      name: 'click',
      element: this,
      detail: addAnalyticsCheckoutFlowMetadata({
        eventName: 'remove-vaulted-card',
        label: 'Remove Vaulted Card',
      }),
    });
  }

  analyticsConfirmDeleteVaultedCard() {
    emit({
      name: 'click',
      element: this,
      detail: addAnalyticsCheckoutFlowMetadata({
        eventName: 'confirm-delete-vaulted-card',
        label: 'Confirm Delete Vaulted Card',
      }),
    });
  }

  renderVaultedCardForm() {
    return this.selectedVaultedCard
      ? html`
          <mhecomm-credit-card-payment-hosted-fields
            id="vaulted-cc"
            amount="${this.totalAmount}"
            clientToken="${this.clientToken}"
            creditCardUniqueId="${this.selectedVaultedCard!.uniqueIdentifier}"
            bin="${this.selectedVaultedCard!.bin}"
            ?threeDsEnabled="${this.threeDsEnabled}"
            vaultedCreditCardNonce="${this.vaultedCreditCardNonce}"
          ></mhecomm-credit-card-payment-hosted-fields>
        `
      : nothing;
  }

  renderEditBillingAddressButton(billingAddress: RmsBookstoreAddress) {
    return html`<button
      href=""
      @click="${this.onClickEditBillingAddressButton}"
      id="billing-edit-link"
      data-automation-id="billing-edit-link"
      class="btn btn-link p-0 ps-1"
      aria-label="${isValidBillingAddress(billingAddress) ? 'Edit Billing Address' : 'Add Address'}"
      >${isValidBillingAddress(billingAddress) ? 'Edit' : 'Add Address'}</button
    >`;
  }

  analyticsEditBillingAddress() {
    emit({
      element: this,
      name: 'click',
      detail: addAnalyticsCheckoutFlowMetadata({
        eventName: 'edit-billing-address',
        label: 'Edit Billing Address',
      }),
    });
  }

  onClickEditBillingAddressButton(e: Event) {
    e.preventDefault();
    this.editVaultedBillingAddress = true;
    store.dispatch(updateEditBillingAddressEditMode(true));
    this.analyticsEditBillingAddress();
    waitForElementsToLoad(this.shadowRoot!, ['mhecomm-address-form']).then(() => {
      this.getAddressForm().prepopulate(this.selectedVaultedCard!.billingAddress!);
    });
  }

  renderSaveCancelButton() {
    return html`
      <div>
        <button type="button" class="btn btn-primary mt-4 mb-2 me-2" @click="${this.onClickSaveButton}">Save</button>

        <button type="button" class="btn btn-outline-primary mt-4 mb-2" @click="${this.onClickCancelButton}">
          Cancel
        </button>
      </div>
    `;
  }

  private async onClickSaveButton() {
    if (!this.isSameAsShippingAddress) {
      this.dispatchEvent(new CustomEvent('form-submit-clear-error', {
        detail: { error: "" },
        bubbles: true,
        composed: true
      }));
      const addressValidationInfo = await this.getAddressForm().validate();
      if (addressValidationInfo) {
        store.dispatch(updateBillingAddress(addressValidationInfo));
      } else {
        const errorMessage = "Please review the errors highlighted below before resubmitting.";
        this.dispatchEvent(new CustomEvent('form-submit-error', {
          detail: { error: errorMessage },
          bubbles: true,
          composed: true
        }));
        throw new Error(errorMessage);
      }
    }
    this.editVaultedBillingAddress = false;
    const updatedVaultedCard: any = {
      ...this.selectedVaultedCard,
      billingAddress: getBillingAddress(store.getState() as StoreState)!,
      hasBillingAddressChanged: true,
    };
    this.selectedVaultedCard = updatedVaultedCard;
    store.dispatch(updateEditBillingAddressEditMode(false));
  }

  private onClickCancelButton() {
    this.editVaultedBillingAddress = false;
    store.dispatch(updateEditBillingAddressEditMode(false));
    waitForElementAndFocus(this.shadowRoot!, "#billing-edit-link");
  }

  onClickNewCreditCardButtonHandler(e: Event) {
    e.preventDefault();
    this.isSameAsShippingAddress = this.shippable && this.showUseShippingAddressButton;
    this.editVaultedBillingAddress = false;
    this.showNewCreditCardForm = true;
    this.selectedVaultedCard = undefined;
    if (this.shippable && this.showUseShippingAddressButton) {
      store.dispatch(updateBillingAddress(getShippingAddress(store.getState() as StoreState)!));
      store.dispatch(updateBillingAddressSameAsShippingAddressFlag(true));
    }
    store.dispatch(updateDisableNextStepButton(false));
    // for a11y when user another card is clicked then focus on first element of credit card hosted field
    wait(500).then(() => {
      this.getCreditCardHostedFields()?.focusOn(CREDIT_CARD_FIELDS.cardholderName);
    })
  }

  /**
   * If billing address is already present, only validate and tokenize credit card form.
   * If billing address is not present, validate both credit card and billing address form.
   */
  public async validatePaymentMethod(): Promise<void> {
    if (
      this.selectedVaultedCard?.uniqueIdentifier !== CreditCardPaymentMethod.NEW_CARD_ID &&
      (this.isSameAsShippingAddress || this.selectedVaultedCard)
    ) {
      await this.tokenizeCreditCard();
    } else if (this.showNewCreditCardForm) {
      store.dispatch(resetPaymentDetails());
      const addressForm = this.getAddressForm();
      const addressValidationInfo = await addressForm.validate();
      if (!addressValidationInfo) {
        throw new Error('Complete all required billing and payment fields before continuing.');
      }
      store.dispatch(updateBillingAddress(addressValidationInfo));
      await this.tokenizeCreditCard();
    } else if (this.selectedVaultedCard?.uniqueIdentifier !== CreditCardPaymentMethod.NEW_CARD_ID) {
      throw new Error('Please select a card or add a new card.');
    }
  }

  /**
   * Tokenize credit card and update payment details in redux.
   */
  async tokenizeCreditCard() {
    await waitForElementsToLoad(this.shadowRoot!, ['mhecomm-credit-card-payment-hosted-fields']);
    const creditCardHostedFieldsComponent = this.getCreditCardHostedFields();
    this.deviceData = getDataCollector(store.getState() as StoreState)?.deviceData;
    if (!this.deviceData) {
      await store
        .dispatch(
          createDataCollector({
            clientToken: this.clientToken,
          })
        )
        .unwrap();
      this.deviceData = getDataCollector(store.getState() as StoreState).deviceData;
    }
    await creditCardHostedFieldsComponent.tokenizeCreditCard();
    const paymentDetails: PaymentDetails = { ...creditCardHostedFieldsComponent.getPaymentDetails() };
    this.updateSelectedCreditCardDetails(creditCardHostedFieldsComponent);
    paymentDetails.paymentType = PaymentType.BRAINTREE_CREDIT_CARD;
    paymentDetails.saveInVault = this.saveInVault;
    paymentDetails.updatePaymentMethodAddress = this.selectedVaultedCard?.hasBillingAddressChanged;
    paymentDetails.deviceData = this.deviceData;
    paymentDetails.creditCardDisplayInfo = this.creditCardDisplayData;
    store.dispatch(updatePaymentDetails(paymentDetails));
  }

  /**
   * Update entered credit card display info.
   * @param creditCardHostedFieldsComponent
   */
  updateSelectedCreditCardDetails(creditCardHostedFieldsComponent: BraintreeHostedFields) {
    if (creditCardHostedFieldsComponent.isVaultedCreditCardForm()) {
      this.creditCardDisplayData = {
        cardType: this.selectedVaultedCard!.cardType,
        cardLast4: this.selectedVaultedCard!.cardLast4,
        bin: this.selectedVaultedCard!.bin,
        expiryMonth: this.selectedVaultedCard!.expiryMonth,
        expiryYear: this.selectedVaultedCard!.expiryYear,
      };
    } else {
      this.creditCardDisplayData = creditCardHostedFieldsComponent.getCreditCardDisplayInfo();
    }
  }

  paymentMethodEditAction(): void {
    this.showNewCreditCardForm = !this.vaultedCardsList.length;
    this.isSameAsShippingAddress = isBillingAddressSameAsShippingAddress(store.getState() as StoreState);
    const paymentDetails = getPaymentDetails(store.getState() as StoreState);
    if (paymentDetails?.creditCardUniqueIdentifier) {
      this.selectedVaultedCard = this.vaultedCardsList.find(
        (vaultedCard) => vaultedCard.uniqueIdentifier === paymentDetails?.creditCardUniqueIdentifier
      );
    }
  }

  getCreditCardHostedFields() {
    return this.shadowRoot!.querySelector('mhecomm-credit-card-payment-hosted-fields') as BraintreeHostedFields;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-credit-card-payment-method': CreditCardPaymentMethod;
  }
}
